// The one pipeline every creation verb funnels through.
//
//   AudioBuffer + metadata
//         │
//         ▼
//   encode WAV → sha256 → putBlob (dedup) → putSample → registerSound
//         │
//         ▼
//     Sample record
//
// Anything above this — mic capture, pattern bounce, synth render,
// mangle chain — just produces an AudioBuffer and calls commit(). That's
// the only shape that matters. New verbs don't touch storage, don't
// touch the engine, don't touch hashing. They hand in a buffer.

import { registerSound } from '../strudel/extensions';
import { sha256Hex } from './storage/hash';
import type { SampleStorage } from './storage/idb';
import { createSamplePlayback } from './playback';
import type { License, Sample, SampleOrigin } from './types';
import { encodeWav } from './wav';

export type CommitInput = {
  buffer: AudioBuffer;
  /** Desired name for `s("...")`. Sanitised to lowercase + underscores
   *  to match superdough's key normalisation. Collisions overwrite. */
  name: string;
  origin: SampleOrigin;
  tags?: string[];
  license?: License;
  pitchedRootMidi?: number;
  sourceSampleIds?: string[];
  /** Pre-assigned id — used by sync to preserve ids across devices.
   *  Omit for brand-new locally-created samples. */
  id?: string;
};

export async function commit(
  input: CommitInput,
  storage: SampleStorage,
  ctx: AudioContext,
): Promise<Sample> {
  const name = sanitizeName(input.name);
  if (!name) throw new Error('commit: name is empty after sanitising');

  const wav = encodeWav(input.buffer);
  const storageKey = await sha256Hex(await wav.arrayBuffer());

  // putBlob is idempotent + race-safe across tabs — see idb.ts.
  await storage.putBlob(storageKey, wav);

  const sample: Sample = {
    id: input.id ?? newId(),
    storageKey,
    name,
    durationMs: Math.round(input.buffer.duration * 1000),
    sampleRate: input.buffer.sampleRate,
    channels: input.buffer.numberOfChannels === 1 ? 1 : 2,
    pitchedRootMidi: input.pitchedRootMidi,
    tags: input.tags ?? [],
    license: input.license ?? 'unspecified',
    origin: input.origin,
    createdAt: Date.now(),
    sourceSampleIds: input.sourceSampleIds,
  };
  await storage.putSample(sample);

  registerSound(
    name,
    createSamplePlayback(ctx, input.buffer, {
      pitchedRootMidi: input.pitchedRootMidi,
    }),
    { type: 'sample', user: true },
  );

  return sample;
}

/** Canonicalise a user-entered name to the key superdough will store it
 *  under. Mirrors superdough's own normalisation so what you see in the
 *  UI equals what you type in `s("…")`. */
export function sanitizeName(raw: string): string {
  return raw
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Node 18 doesn't ship crypto.randomUUID on globalThis by default;
  // this fallback keeps tests green under older runtimes.
  return 'id-' + Date.now() + '-' + Math.random().toString(36).slice(2, 12);
}
